import React, { useState } from 'react';
import { PlusCircle, Trash2, Edit2, Check, X, MessageSquare } from 'lucide-react';
import { useChat, Conversation } from '../contexts/ChatContext';

interface ChatSidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
  isMobile?: boolean;
}

export const ChatSidebar: React.FC<ChatSidebarProps> = ({ isOpen = true, onClose = () => {}, isMobile = false }) => {
  const { 
    conversations, 
    currentConversationId, 
    setCurrentConversationId,
    createNewConversation,
    updateConversationTitle,
    deleteConversation,
    isLoading
  } = useChat();
  
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');

  const handleEditStart = (conv: Conversation) => {
    setEditingId(conv.id);
    setEditTitle(conv.title);
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditTitle('');
  };

  const handleEditSave = async (id: string) => {
    if (editTitle.trim()) {
      await updateConversationTitle(id, editTitle.trim());
    }
    setEditingId(null);
  };

  const handleNewChat = async () => {
    await createNewConversation();
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this conversation?')) {
      await deleteConversation(id);
    }
  };

  // If sidebar is not open (for mobile), don't render anything
  if (isMobile && !isOpen) {
    return null;
  }

  return (
    <div className={`flex flex-col h-full bg-[#0F1012] text-white w-full transition-all ${isMobile ? 'fixed inset-0 z-50' : ''}`}>
      {isMobile && (
        <div className="flex justify-end p-2">
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>
      )}
      <div className="p-3">
        <button
          onClick={handleNewChat}
          className="flex items-center justify-center w-full py-3 px-3 border border-gray-800 hover:bg-gray-900 rounded-md transition-colors gap-2 text-sm"
        >
          <PlusCircle size={16} />
          New chat
        </button>
      </div>
      
      <div className="flex-1 overflow-y-auto px-2 py-2">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#00B5A1]"></div>
          </div>
        ) : conversations.length === 0 ? (
          <div className="text-center text-gray-500 mt-8 px-4">
            <MessageSquare className="mx-auto mb-2 opacity-30" size={24} />
            <p className="text-sm">No conversations yet</p>
          </div>
        ) : (
          <ul className="space-y-1">
            {conversations.map((conv) => (
              <li key={conv.id} className="group">
                <div
                  className={`${
                    currentConversationId === conv.id
                      ? 'bg-[#232429]'
                      : 'hover:bg-[#1A1B1E]'
                  } rounded-md cursor-pointer transition-colors`}
                >
                  {editingId === conv.id ? (
                    <div className="p-3 flex items-center gap-2">
                      <input
                        type="text"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        className="flex-1 bg-[#1E1F25] border border-gray-800 rounded-md px-2 py-1 text-white text-sm focus:outline-none focus:ring-1 focus:ring-[#00B5A1]"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleEditSave(conv.id);
                          if (e.key === 'Escape') handleEditCancel();
                        }}
                      />
                      <button
                        onClick={() => handleEditSave(conv.id)}
                        className="text-[#00B5A1] hover:text-[#00b5a1cc]"
                      >
                        <Check size={16} />
                      </button>
                      <button
                        onClick={handleEditCancel}
                        className="text-gray-400 hover:text-gray-300"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div
                      className="p-3 flex items-center relative"
                      onClick={() => setCurrentConversationId(conv.id)}
                    >
                      <div className="mr-3">
                        <MessageSquare size={16} className="text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate pr-8">{conv.title}</p>
                      </div>
                      
                      <div className="absolute right-2 flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditStart(conv);
                          }}
                          className="p-1 text-gray-400 hover:text-gray-200 rounded"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={(e) => handleDelete(conv.id, e)}
                          className="p-1 text-gray-400 hover:text-red-400 rounded ml-1"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      
      <div className="px-4 py-3 text-xs text-gray-500 border-t border-gray-800">
        <a 
          href="https://yesilhealth.com" 
          target="_blank" 
          rel="noopener noreferrer"
          className="flex items-center hover:text-[#00B5A1] transition-colors"
        >
          <span className="mr-1">Yesil Health AI</span>
        </a>
      </div>
    </div>
  );
}; 